import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, X, Sheet } from "lucide-react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const formatDetail = (d) => {
  if (d == null) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return (d && d.msg) || String(d);
};

export const apiFetch = async (path, options = {}) => {
  let res = await fetch(`${API}${path}`, { credentials: "include", ...options });
  if (res.status === 401) {
    const r = await fetch(`${API}/auth/refresh`, { method: "POST", credentials: "include" });
    if (r.ok) res = await fetch(`${API}${path}`, { credentials: "include", ...options });
  }
  return res;
};

export const postReply = async (kind, id, message) => {
  const res = await apiFetch("/admin/reply", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ kind, record_id: id, message }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(formatDetail(body.detail));
  return body.reply;
};

export const downloadCsv = async (path, filename) => {
  try {
    const res = await apiFetch(path);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(formatDetail(body.detail));
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Download started.", { description: filename });
  } catch (err) {
    toast.error("Could not export.", { description: err.message });
  }
};

export const ReplyBox = ({ testId, sending, value, onChange, onSend, onCancel }) => (
  <div className="mt-4 rounded-xl border border-[#E2E8F0] bg-[#F2F4F7] p-4">
    <label className="field-label" htmlFor={`reply-input-${testId}`}>Your reply <span className="text-[#1E3A8A]">*</span></label>
    <textarea
      id={`reply-input-${testId}`}
      rows={3}
      value={value}
      onChange={onChange}
      data-testid={`reply-input-${testId}`}
      className="field resize-none"
      placeholder="Write your reply — it is emailed to them, and replies come back to aflimart.tech@gmail.com"
    />
    <div className="mt-3 flex gap-3">
      <button onClick={onSend} disabled={sending || !value.trim()} data-testid={`reply-send-${testId}`} className="btn-gold !px-5 !py-2 !text-xs disabled:cursor-not-allowed disabled:opacity-50">
        {sending ? <Loader2 size={14} className="animate-spin" /> : <><Send size={13} /> Send Reply</>}
      </button>
      <button onClick={onCancel} data-testid={`reply-cancel-${testId}`} className="btn-ghost !px-5 !py-2 !text-xs">Cancel</button>
    </div>
  </div>
);

export const ReplyHistory = ({ replies, testId }) =>
  replies && replies.length > 0 ? (
    <div className="mt-4 space-y-2 border-t border-[#E2E8F0] pt-3" data-testid={`reply-history-${testId}`}>
      <p className="font-sans text-[10px] tracking-[0.2em] text-[#94A3B8]">REPLIES SENT</p>
      {replies.map((r, j) => (
        <p key={j} className="text-xs leading-relaxed text-[#64748B]">
          <span className="text-[#CBD5E1]">{r.sent_at ? new Date(r.sent_at).toLocaleString() : ""}:</span> {r.message}
        </p>
      ))}
    </div>
  ) : null;

export const DetailModal = ({ title, onClose, testId, children }) => (
  <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-6" onClick={onClose} data-testid={testId}>
    <div
      className="card-dark max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-b-none p-6 hover:transform-none sm:rounded-2xl sm:p-8"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-4">
        <h2 className="font-sans text-xl text-[#0F172A] sm:text-2xl">{title}</h2>
        <button
          onClick={onClose}
          data-testid={`${testId}-close`}
          aria-label="Close"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#E2E8F0] text-[#64748B] transition-colors hover:border-[#1E3A8A]/40 hover:text-[#1E3A8A]"
        >
          <X size={16} />
        </button>
      </div>
      <div className="mt-6">{children}</div>
    </div>
  </div>
);

export const SheetsConnectCard = ({ testId, status }) => {
  const [starting, setStarting] = useState(false);
  const unconfigured = status && status.configured === false;

  const connect = async () => {
    setStarting(true);
    try {
      const res = await apiFetch("/admin/sheets/connect");
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.url) {
        window.location.href = body.url;
        return;
      }
      throw new Error(formatDetail(body.detail));
    } catch (err) {
      toast.error("Could not start Google connection.", { description: err.message });
      setStarting(false);
    }
  };

  return (
    <div className="card-dark flex flex-col items-center p-12 text-center hover:transform-none" data-testid={testId}>
      <Sheet size={28} className="text-[#1E3A8A]/60" />
      <p className="mt-4 font-sans text-xl text-[#0F172A]">Connect Google Sheets</p>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-[#94A3B8]">
        These submissions are collected in your official Google Forms and stored in Google Sheets. Connect the Google
        account that owns the tracker sheets to see every record here — read-only, your sheets are never changed.
      </p>
      {unconfigured && (
        <p className="mt-4 rounded-lg border border-[#1E3A8A]/30 bg-[#1E3A8A]/5 px-4 py-3 text-xs leading-relaxed text-[#64748B]" data-testid={`${testId}-unconfigured`}>
          Server setup is pending — the Google connection keys have not been added yet.
        </p>
      )}
      <button
        onClick={connect}
        disabled={starting || unconfigured}
        data-testid={`${testId}-connect-button`}
        className="btn-gold mt-6 !px-6 !py-2.5 !text-xs disabled:cursor-not-allowed disabled:opacity-50"
      >
        {starting ? <Loader2 size={14} className="animate-spin" /> : "Connect Google Sheets"}
      </button>
    </div>
  );
};
